import Tippy from '@tippyjs/react'
import { selectCurrentHotelName } from 'accessors'
import {
  PrimarySmallButton,
  SecondarySmallButton
} from 'components/ReusableComponents/Button/SmallButtons'
import AnalyticsIcon from 'components/ReusableComponents/Icons/Analytics'
import CopyIcon from 'components/ReusableComponents/Icons/CopyIcon'
import EditIcon from 'components/ReusableComponents/Icons/Edit'
import QuestionIcon from 'components/ReusableComponents/Icons/Question'
import Trash from 'components/ReusableComponents/Icons/Trash'
import {
  customCampaignStatusTypes as statusTypes,
  getCampaignStatusColor
} from 'features/CustomCampaigns/helpers'
import { campaignTemplateToText } from 'features/ManageCampaigns/CampaignCard/templateToText'
import { ReactComponent as Text } from 'features/ManageCampaigns/CampaignCard/text.svg'
import usePlausible from 'hooks/usePlausible'
import moment from 'moment'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useHistory } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import campaignApi from 'services/api/campaign'

import CampaignResults from '../../CampaignResults'
import styles from './CampaignCard.module.scss'

function getDateRange(date1, date2) {
  if (moment(date1).format('MMM') === moment(date2).format('MMM')) {
    return `${moment(date1).format('MMM')} ${moment(date1).format('D')}-${moment(
      date2
    ).format('D')} ${moment(date2).format('YYYY')}`
  } else if (moment(date1).format('YYYY') === moment(date2).format('YYYY')) {
    return `${moment(date1).format('MMM')} ${moment(date1).format('D')} - ${moment(
      date2
    ).format('MMM')} ${moment(date2).format('D')} ${moment(date2).format('YYYY')}`
  } else {
    return `${moment(date1).format('MMM D, YYYY')} - ${moment(date2).format(
      'MMM D, YYYY'
    )}`
  }
}

const CampaignCard = ({
  id,
  name,
  finishDate,
  remaining = 0,
  size = 0,
  startDate,
  messageTemplate = '',
  customDeliveryStatus,
  refreshResults,
  audienceId,
  messageMediaUrls,
  promotionCode,
  minNightsToStay,
  maxDiscountPercentage,
  promotionTagline,
  addedBy,
  addedOn,
  updatedBy,
  updatedOn
}) => {
  const userName = useSelector(s => s.login.currentUser.name)
  const userPhone = useSelector(s => s.login.currentUser.phone)
  const hotelName = useSelector(selectCurrentHotelName)

  const [isShowingResults, setIsShowingResults] = useState(false)

  const history = useHistory()

  const { addToast } = useToasts()

  const { postEvent, plausibleEvents } = usePlausible()

  function showResults() {
    postEvent(plausibleEvents.ViewTextBlastResults, {
      id
    })
    setIsShowingResults(true)
  }

  function cloneCampaign() {
    campaignApi.addCustomCampaign({ audienceId }).then(res =>
      campaignApi
        .updateCustomCampaign({
          contentId: res.item.contentId,
          messageTemplate,
          messageMediaUrls,
          promotionCode,
          minNightsToStay,
          maxDiscountPercentage,
          promotionTagline
        })
        .then(res => {
          history.push(`/custom-campaigns/${res.contentId}`)
        })
        .then(() => {
          addToast(`Succesfuly cloned custom campaign`, {
            appearance: 'success',
            autoDismiss: true
          })
        })
        .catch(err => {
          addToast(`Failed to clone: ${err.toString()}`, {
            appearance: 'error'
          })
        })
    )
  }

  function sendTestMessage() {
    campaignApi
      .sendTestSms({ contentCategory: 'Custom', contentId: id })
      .then(() => {
        addToast(`We've sent a test message to ${userPhone}`, {
          appearance: 'success',
          autoDismiss: true
        })
      })
      .catch(() => {
        addToast(`Failed to send a message to ${userPhone}`, {
          appearance: 'error',
          autoDismiss: true
        })
      })
  }

  function deleteCampaign() {
    campaignApi
      .updateCustomCampaign({
        contentId: id,
        customDeliveryStatus: statusTypes.Deleted
      })
      .then(() => {
        refreshResults()
      })
      .catch(() => {
        addToast(`Failed to delete campaign`, { appearance: 'error', autoDismiss: true })
      })
  }

  return (
    <>
      {isShowingResults && (
        <CampaignResults id={id} onClose={() => setIsShowingResults(false)} />
      )}
      <div className={styles.card}>
        <div className={styles.header}>
          <div className={styles.headerPrimary}>
            <div className={styles.titleRow}>
              <h3 className={styles.title}>{name}</h3>
              {startDate && finishDate && (
                <p className={styles.dates}>{getDateRange(startDate, finishDate)}</p>
              )}
              {startDate && !finishDate && (
                <p className={styles.dates}>{moment(startDate).format('MMM D, YYYY')}</p>
              )}
              {!finishDate && !startDate && <p className={styles.dates}>Dates not set</p>}
            </div>
            <div className={styles.descRow}>
              <span className={styles.id}>#{id}</span>
              <p
                className={styles.status}
                data-testid="campaign-status"
                style={{ color: getCampaignStatusColor(customDeliveryStatus) }}
              >
                <span className={styles.dot}>
                  <svg height="8" width="7">
                    <circle
                      cx="3.5"
                      cy="3.5"
                      r="3.5"
                      fill={getCampaignStatusColor(customDeliveryStatus)}
                    />
                  </svg>
                </span>
                {customDeliveryStatus}
              </p>
            </div>
          </div>
          <div className={styles.headerSecondary}>
            <div>
              Created by&nbsp;
              <Tippy content={moment(addedOn).format('MMM DD, YYYY')}>
                <span className={styles.hoverDate}>{addedBy}</span>
              </Tippy>
              {!moment(addedOn).isSame(moment(updatedOn), 'day') && (
                <>
                  , last updated by{' '}
                  <Tippy content={moment(updatedOn).format('MMM DD, YYYY')}>
                    <span className={styles.hoverDate}>{updatedBy}</span>
                  </Tippy>
                </>
              )}
            </div>
          </div>
        </div>
        <div className={styles.text}>
          <p className={styles.textCopy}>
            {messageTemplate &&
              campaignTemplateToText(messageTemplate, userName, hotelName, false)}
          </p>
          <div className={styles.textControls}>
            <button onClick={sendTestMessage}>
              <Text />
              Test
            </button>
          </div>
        </div>

        <div className={styles.progress}>
          <progress id="file" value={size - remaining} max={size}></progress>
          <div className={styles.progressInfo}>
            <span className={styles.percentage}>
              {size === 0 ? 0 : Math.floor(((size - remaining) * 100) / size)}% sent
            </span>
            <span>
              {size - remaining}/{size} texts{' '}
            </span>
            {customDeliveryStatus === 'Completed' && size - remaining > 0 && (
              <>
                <QuestionIcon className={styles.tooltipIcon} />
                <div className={styles.tooltip}>
                  <p className={styles.tooltipTitle}>
                    Why this text blast was not sent to everyone?
                  </p>
                  <p className={styles.tooltipP}>We're not reaching out to guests who:</p>
                  <ul className={styles.tooltipList}>
                    <li>Already have a reservation.</li>
                    <li>Stayed at the hotel in last 7 days.</li>
                    <li>Already received a text in the last 14 days.</li>
                    <li>Explicitly opted out of receiving texts from the hotel.</li>
                  </ul>
                  <p className={styles.tooltipP}>
                    Since the status of guests might change while the text blast is
                    active, we might not reach out to all guests in the audience.
                  </p>
                  <p className={styles.tooltipP}>
                    Also, since we're sending a limited number of texts per day due to
                    carrier restrictions, using a cut-off date might result in some guests
                    not receiving the text.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
        <div className={styles.promo}>
          {[statusTypes.Completed, statusTypes.Interrupted, statusTypes.Started].includes(
            customDeliveryStatus
          ) && (
            <PrimarySmallButton onClick={showResults} className={styles.resultsButton}>
              <AnalyticsIcon
                fill="#FFFFFF"
                width={24}
                height={24}
                className={styles.buttonIcon}
              />
              See results
            </PrimarySmallButton>
          )}
          {[statusTypes.Completed, statusTypes.Interrupted].includes(
            customDeliveryStatus
          ) && (
            <SecondarySmallButton onClick={cloneCampaign} className={styles.abortButton}>
              <CopyIcon fill="#a59696" width={15} className={styles.buttonIcon} />
              Clone
            </SecondarySmallButton>
          )}
          {[statusTypes.Interrupted, statusTypes.Completed, statusTypes.Draft].includes(
            customDeliveryStatus
          ) && (
            <SecondarySmallButton className={styles.abortButton} onClick={deleteCampaign}>
              <Trash
                fill="#a59696"
                width={17}
                height={19}
                className={styles.buttonIcon}
              />
              Delete
            </SecondarySmallButton>
          )}
          {![statusTypes.Interrupted, statusTypes.Completed].includes(
            customDeliveryStatus
          ) && (
            <Link to={`/custom-campaigns/${id}`}>
              <PrimarySmallButton className={styles.editButton}>
                <EditIcon fill="white" className={styles.buttonIcon} width={17} />
                Edit
              </PrimarySmallButton>
            </Link>
          )}
        </div>
      </div>
    </>
  )
}

CampaignCard.propTypes = {
  id: PropTypes.number,
  finishDate: PropTypes.string,
  name: PropTypes.string,
  remaining: PropTypes.number,
  size: PropTypes.number,
  startDate: PropTypes.string
}

export default CampaignCard
